import { applyStatusEffect } from '../ability/statuses/apply-status-effect.js';
import { carrierHasTrigger, layerListEntryUuid } from '../ability/statuses/status-trigger-gate.js';
import { storedStatusHasSpecialTeardown } from '../ability/statuses/status-effect-removal-helpers.js';
import { HandBack, activeStatuses, record, upper, type JanusCtx, type Row } from '../ability/context.js';
import { readCreaturePayloadStatuses, STATUS_GRANT_LAYERS } from '../compile/creature-status-reader.js';
import { statusRows, statusItemId, statusRemovalBefore } from '../statuses/expiry.js';
import { isSelfStatusOrigin } from '../statuses/status-origin.js';
import { inventoryField } from '../../shared/array-types.js';

export const INVENTORY_HIDDEN_ID = '47667406-1B72-9D60-310B-D8CF6BFE397D';
export interface InventoryHiddenPlan {
  schema: 'janus.inventory.hidden.v1';
  before: Array<Row & { status_id: string }>;
  writes: Array<{ kind: 'hidden-mark'; status_id: string; status_connected_id: string }
    | { kind: 'status-remove'; status_id: string }>;
}

/** Item visibility is permanent and self-cast. Item gain/loss listeners have no creature executor;
 * refuse an actually matching listener before paint, rather than silently losing its dispatch. */
export function planInventoryHidden(row: Row, desired: 0 | 1, encounter: Row,
  members: ReadonlyMap<string, Row>, readers: JanusCtx['read']): InventoryHiddenPlan {
  const id = upper(inventoryField(row, 'inventory_id'));
  if (!id) throw new HandBack('inventory-hidden:item-not-held');
  const target = { ...row, encounter_arr: encounter,
    statuses: { ...record(row.statuses), target: statusRows(row, 'target').map(status => ({ ...status })) } };
  const read = { ...readers, statuses: (holder: Row): Row[] => Object.values(readCreaturePayloadStatuses(holder,
    (r, key) => readers.value(record(r), key), readers.definition,
    owner => members.get(upper(owner)) ?? null)).map(record).filter((s): s is Row => s !== null) };
  const ctx: JanusCtx = { encounter, members: new Map(members), originals: new Map(members), read,
    roll: { d: () => { throw new HandBack('inventory-hidden:unexpected-roll'); },
      fraction: () => { throw new HandBack('inventory-hidden:unexpected-roll'); } },
    rows: [], statusesAdded: [], statusesRemoved: [], chargesChanged: [], inventoryChanged: [], statusConnectedId: null,
    rollPrivate: 0, deathTail: null, concentrationBreaks: null, statusStacks: [] };
  const held = statusRows(target, 'target').filter(status => Number(statusRemovalBefore(status, id).status_deleted) === 0);
  const hidden = held.filter(status => statusItemId(status) === INVENTORY_HIDDEN_ID);
  const plan: InventoryHiddenPlan = { schema: 'janus.inventory.hidden.v1', before: held.map(status => ({
    status_id: upper(status.status_id), ...statusRemovalBefore(status, id) })), writes: [] };
  if (held.length > 128) throw new HandBack('inventory-hidden:status-snapshot-too-large');
  if (desired === 1 && !hidden.length) {
    // The native creature removal executor cannot run an item's end-on-gain tail. Only a
    // definition actually naming Hidden makes this duty due; ordinary held effects proceed.
    for (const status of activeStatuses(ctx, target)) {
      const before = statusRemovalBefore(status, id);
      const origin = isSelfStatusOrigin(upper(status.status_id), upper(status.status_origin_id)) ? '' : upper(status.status_origin_id);
      for (const source of new Set([String(before.status_item_id), origin].filter(Boolean))) {
        const definition = read.definition(source) ?? record(status.status_arr);
        if (definition && read.entries(definition, 'effect_ends_when_target_gains_status_effect')
          .some(entry => layerListEntryUuid(entry) === INVENTORY_HIDDEN_ID))
          throw new HandBack('inventory-hidden:item-end-on-gain-not-ported');
      }
    }
    applyStatusEffect(ctx, { casterId: id, targetIds: [id], abilityId: INVENTORY_HIDDEN_ID,
      rawDiceMod: 0, rawDiceAdvDisStacks: 0, useAbilityVariable1: null, useAbilityVariable2: null, castingTargets: '' },
    {}, INVENTORY_HIDDEN_ID, 'self_status_effect_applied', target, target,
    { ends: '0-0-0-0-0-0', flags: [0, 0, 0, 0] }, false, false, 0, false, '');
    if (ctx.rows.length || ctx.statusesAdded.length !== 1 || ctx.statusesRemoved.length)
      throw new HandBack('inventory-hidden:application-produced-no-mark');
    const added = ctx.statusesAdded[0]!;
    if (added.status_aoe_type || added.status_forced_roll_extreme
      || [added.status_aoe_distance, added.status_aoe_distance_depth, added.status_aoe_distance_height].some(value => Number(value ?? 0) !== 0))
      throw new HandBack('inventory-hidden:mark-payload-not-ported');
    plan.writes.push({ kind: 'hidden-mark', status_id: read.uuid(), status_connected_id: added.status_connected_id });
  } else if (desired === 0 && hidden.length) {
    // The canonical item remover fires the loser's hook, then retires every SAME-TARGET
    // connection peer as raw rows. Another holder on that connection is left alone.
    target.statuses.target = held.filter(status => statusItemId(status) !== INVENTORY_HIDDEN_ID);
    if (carrierHasTrigger(ctx, target, 'triggers_when_caster_loses_status_effect', 'inventory-hidden:loss-trigger', INVENTORY_HIDDEN_ID))
      throw new HandBack('inventory-hidden:item-loss-trigger-not-ported');
    const definition = read.definition(INVENTORY_HIDDEN_ID);
    if (!definition) throw new HandBack('inventory-hidden:hidden-definition-not-held');
    if (hidden.some(status => storedStatusHasSpecialTeardown(ctx, status))
      || ['object_destroyed_after_duration_complete', 'ability_charges'].some(key => read.has(definition, key))
      || STATUS_GRANT_LAYERS.some(key => read.entries(definition, key).length > 0))
      throw new HandBack('inventory-hidden:item-removal-tail-not-ported');
    const connections = new Set(hidden.map(status => String(statusRemovalBefore(status, id).status_connected_id)));
    plan.writes = held.filter(status => connections.has(String(statusRemovalBefore(status, id).status_connected_id)))
      .map(status => ({ kind: 'status-remove', status_id: upper(status.status_id) }));
  }
  return plan;
}
